// screens-features.tsx - Marketing: Features overview (six pillars, deep sections)
// Exports: ScreenFeatures, FEATURES_OVERVIEW
import { Link } from '../design/router';
import { Arrow, Check, CtaBand, MarketingFooter, MarketingNav } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';
import { FEATURE_PILLARS, FEATURE_PILLAR_ORDER } from './screens-feature-pages';

export interface FeatureOverviewItem {
  key: string;
  eyebrow: string;
  title: string;
  to: string;
  placeholder: string;
  copy: string;
  points: string[];
}

/** One overview entry per pillar page, derived from the same data the pillar
 * pages render, so the two never drift apart. */
export const FEATURES_OVERVIEW: FeatureOverviewItem[] = FEATURE_PILLAR_ORDER.map((key) => {
  const p = FEATURE_PILLARS[key]!;
  return {
    key,
    eyebrow: p.n + ' · ' + p.name,
    title: p.h1,
    to: '/features/' + key,
    placeholder: p.heroSlot,
    copy: p.sub,
    points: p.sections.slice(0, 3).map((s) => s.t),
  };
});

function FeatureSection({ f, flip }: { f: FeatureOverviewItem; flip: boolean }) {
  const name = f.eyebrow.split('· ')[1] ?? f.eyebrow;
  return (
    <section style={{ padding: '96px clamp(20px, 6vw, 56px)', borderTop: '1px solid var(--ss-border-soft)' }}>
      <div className="ss-grid-collapse" style={{
        maxWidth: 1180, margin: '0 auto', display: 'grid',
        gridTemplateColumns: flip ? '1fr 440px' : '440px 1fr', gap: 72, alignItems: 'center',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, order: flip ? 2 : 1 }}>
          <span className="ss-eyebrow">{f.eyebrow}</span>
          <h2 className="ss-display" style={{ fontSize: 40 }}>{f.title}</h2>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 16.5, textWrap: 'pretty' }}>{f.copy}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 8 }}>
            {f.points.map((p) => (
              <div key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: 'var(--ss-text-2)' }}>
                <span style={{ paddingTop: 3 }}><Check /></span>
                <span>{p}</span>
              </div>
            ))}
          </div>
          <Link to={f.to} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--ss-text)', marginTop: 10 }}>
            Explore {name} <Arrow />
          </Link>
        </div>
        <div className="ss-card" style={{ padding: 10, order: flip ? 1 : 2, background: 'var(--ss-panel-strong)' }}>
          <ImageSlot shape="rounded" radius={12}
            placeholder={f.placeholder}
            style={{ width: '100%', height: 400, display: 'block' }} />
        </div>
      </div>
    </section>
  );
}

export function ScreenFeatures() {
  const features = FEATURES_OVERVIEW;
  return (
    <div className="ss-screen">
      <MarketingNav active="Features" />
      <header style={{ padding: '96px clamp(20px, 6vw, 56px) 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse 70% 55% at 50% -10%, var(--ss-glow), transparent 70%)',
        }} />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <span className="ss-eyebrow">The studio</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2.1rem, 8vw, 3.625rem)' }}>One studio, the whole game.</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 17, maxWidth: 580, textWrap: 'pretty' }}>
            Scene, art, characters, rules, multiplayer and hosting, in one browser tab. Here is what each part does.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 6 }}>
            {features.map((f) => (
              <Link key={f.key} to={f.to} className="range-chip" style={{ fontSize: 11.5, padding: '6px 14px' }}>{f.eyebrow.split('· ')[1]}</Link>
            ))}
          </div>
        </div>
      </header>
      {features.map((f, i) => <FeatureSection key={f.key} f={f} flip={i % 2 === 1} />)}
      <CtaBand />
      <MarketingFooter />
    </div>
  );
}
